'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function IraCalculator() {
  const t = useTranslations("tools.iraCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  const [inputs, setInputs] = useState({
    currentBalance: 20000,
    annualContribution: 7000,
    rateOfReturn: 6,
    currentAge: 30,
    retirementAge: 65,
    currentTaxRate: 25,
    retirementTaxRate: 15,
  });

  const [results, setResults] = useState<any>(null);
  const [chartData, setChartData] = useState<any[]>([]);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const formatCurrency = (value: number) => {
    return '$' + Math.round(value).toLocaleString('en-US');
  };

  const calculateIRA = () => {
    const { currentBalance, annualContribution, rateOfReturn, currentAge, retirementAge, currentTaxRate, retirementTaxRate } = inputs;

    const years = retirementAge - currentAge;
    const rate = rateOfReturn / 100;
    const currentTax = currentTaxRate / 100;
    const retirementTax = retirementTaxRate / 100;

    let traditionalBalance = currentBalance;
    let rothBalance = currentBalance;
    let taxableBalance = currentBalance;

    const yearlyData = [{
      age: currentAge,
      traditional: currentBalance,
      traditionalAfterTax: currentBalance * (1 - retirementTax),
      roth: currentBalance,
      taxable: currentBalance
    }];

    for (let i = 0; i < years; i++) {
      traditionalBalance = traditionalBalance * (1 + rate) + annualContribution;

      const rothContribution = annualContribution * (1 - currentTax);
      rothBalance = rothBalance * (1 + rate) + rothContribution;

      const taxableContribution = annualContribution * (1 - currentTax);
      const taxableGrowth = taxableBalance * rate;
      const capitalGainsTax = taxableGrowth * 0.15;
      taxableBalance = taxableBalance + taxableGrowth - capitalGainsTax + taxableContribution;

      yearlyData.push({
        age: currentAge + i + 1,
        traditional: traditionalBalance,
        traditionalAfterTax: traditionalBalance * (1 - retirementTax),
        roth: rothBalance,
        taxable: taxableBalance
      });
    }

    const traditionalAfterTax = traditionalBalance * (1 - retirementTax);
    const rothAfterTax = rothBalance;
    const taxableAfterTax = taxableBalance;

    return {
      traditionalBalance,
      traditionalAfterTax,
      rothBalance,
      rothAfterTax,
      taxableBalance,
      taxableAfterTax,
      yearlyData
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    if (inputs.retirementAge <= inputs.currentAge) {
      alert(t('errorRetirementAge'));
      return;
    }

    const calculatedResults = calculateIRA();
    setResults(calculatedResults);
    setChartData(calculatedResults.yearlyData);

    setTimeout(() => {
      const resultsElement = document.getElementById('results-section');
      if (resultsElement) {
        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 100);
  };

  const handleClear = () => {
    setInputs({
      currentBalance: 20000,
      annualContribution: 7000,
      rateOfReturn: 6,
      currentAge: 30,
      retirementAge: 65,
      currentTaxRate: 25,
      retirementTaxRate: 15,
    });
    setResults(null);
    setChartData([]);
  };

  const handleInputChange = (field: string, value: string) => {
    setInputs(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    { marginBottom: '1.5rem' },
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '2rem', marginTop: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const formSectionStyle = {
    marginBottom: '1.5rem'
  };

  const formSectionTitleStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      color: '#1e293b',
      marginBottom: '1rem',
      borderBottom: '2px solid #e2e8f0',
      paddingBottom: '0.5rem'
    },
    { fontSize: '1.1rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.25rem'
  };

  const labelStyle = getResponsiveStyle(
    {
      display: 'block',
      marginBottom: '0.5rem',
      color: '#334155',
      fontWeight: 500
    },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1.05rem' },
    { padding: '1rem', fontSize: '1.1rem' }
  );

  const inputWithPrefixStyle = {
    position: 'relative' as const
  };

  const inputPrefixStyle = {
    position: 'absolute' as const,
    left: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none' as const
  };

  const inputSuffixStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none' as const
  };

  const helpTextStyle = getResponsiveStyle(
    { color: '#64748b', marginTop: '0.25rem', fontStyle: 'italic' as const },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' }
  );

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '2rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
      color: 'white'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1.125rem 2rem', fontSize: '1.1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem', minHeight: '44px' },
    { padding: '1rem 1.75rem', fontSize: '1.05rem' },
    { padding: '1.125rem 2rem', fontSize: '1.1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { fontWeight: 700, color: '#0f172a', marginBottom: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.4rem' },
    { fontSize: '1.5rem' }
  );

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    marginBottom: '1.5rem'
  };

  const thStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      padding: '0.875rem',
      textAlign: 'left' as const,
      fontWeight: 600,
      color: '#1e293b',
      borderBottom: '2px solid #e2e8f0'
    },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' }
  );

  const tdStyle = getResponsiveStyle(
    {
      padding: '0.875rem',
      borderBottom: '1px solid #e2e8f0',
      color: '#334155'
    },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const amountValueStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#059669' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' },
    { fontSize: '1.05rem' }
  );

  const summaryBoxStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #10b981',
      marginTop: '1.5rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const summaryPStyle = getResponsiveStyle(
    { color: '#065f46', lineHeight: 1.6, margin: '0.5rem 0' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' }
  );

  const chartContainerStyle = getResponsiveStyle(
    {
      marginTop: '2rem',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2rem', borderRadius: '1.5rem' }
  );

  const chartTitleStyle = getResponsiveStyle(
    { fontWeight: 600, color: '#0f172a', marginBottom: '1.5rem', textAlign: 'center' as const },
    { fontSize: '1.15rem' },
    { fontSize: '1.2rem' },
    { fontSize: '1.25rem' }
  );

  const chartLegendStyle = getResponsiveStyle(
    { display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '1rem', flexWrap: 'wrap' as const },
    { gap: '1rem' },
    { gap: '1.25rem' },
    { gap: '1.5rem' }
  );

  const legendItemStyle = getResponsiveStyle(
    { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '0.8rem' },
    { fontSize: '0.85rem' },
    { fontSize: '0.9rem' }
  );

  const legendColorStyle = {
    width: '20px',
    height: '20px',
    borderRadius: '4px'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolHeaderStyle}>
          <h2 style={h2Style}>{t('toolTitle')}</h2>
          <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
        </div>

        <div style={calculatorLayoutStyle}>
          <div>
            <form style={calculatorFormStyle} onSubmit={handleCalculate}>
              <div style={formSectionStyle}>
                <h3 style={formSectionTitleStyle}>{t('accountInfoTitle')}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="current-balance">{t('currentBalanceLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="current-balance"
                      value={inputs.currentBalance}
                      onChange={(e) => handleInputChange('currentBalance', e.target.value)}
                      min="0"
                      step="1000"
                      required
                      style={{...inputStyle, paddingLeft: '2rem'}}
                    />
                  </div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="annual-contribution">{t('annualContributionLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <span style={inputPrefixStyle}>$</span>
                    <input
                      type="number"
                      id="annual-contribution"
                      value={inputs.annualContribution}
                      onChange={(e) => handleInputChange('annualContribution', e.target.value)}
                      min="0"
                      max="50000"
                      step="500"
                      required
                      style={{...inputStyle, paddingLeft: '2rem'}}
                    />
                  </div>
                  <div style={helpTextStyle}>{t('contributionLimitHelp')}</div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="rate-of-return">{t('rateOfReturnLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <input
                      type="number"
                      id="rate-of-return"
                      value={inputs.rateOfReturn}
                      onChange={(e) => handleInputChange('rateOfReturn', e.target.value)}
                      min="0"
                      max="20"
                      step="0.1"
                      required
                      style={{...inputStyle, paddingRight: '2rem'}}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>

              <div style={formSectionStyle}>
                <h3 style={formSectionTitleStyle}>{t('ageTimelineTitle')}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="current-age">{t('currentAgeLabel')}</label>
                  <input
                    type="number"
                    id="current-age"
                    value={inputs.currentAge}
                    onChange={(e) => handleInputChange('currentAge', e.target.value)}
                    min="18"
                    max="100"
                    required
                    style={inputStyle}
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="retirement-age">{t('retirementAgeLabel')}</label>
                  <input
                    type="number"
                    id="retirement-age"
                    value={inputs.retirementAge}
                    onChange={(e) => handleInputChange('retirementAge', e.target.value)}
                    min="50"
                    max="100"
                    required
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={formSectionStyle}>
                <h3 style={formSectionTitleStyle}>{t('taxRatesTitle')}</h3>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="current-tax-rate">{t('currentTaxRateLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <input
                      type="number"
                      id="current-tax-rate"
                      value={inputs.currentTaxRate}
                      onChange={(e) => handleInputChange('currentTaxRate', e.target.value)}
                      min="0"
                      max="50"
                      step="0.5"
                      required
                      style={{...inputStyle, paddingRight: '2rem'}}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                  <div style={helpTextStyle}>{t('currentTaxRateHelp')}</div>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="retirement-tax-rate">{t('retirementTaxRateLabel')}</label>
                  <div style={inputWithPrefixStyle}>
                    <input
                      type="number"
                      id="retirement-tax-rate"
                      value={inputs.retirementTaxRate}
                      onChange={(e) => handleInputChange('retirementTaxRate', e.target.value)}
                      min="0"
                      max="50"
                      step="0.5"
                      required
                      style={{...inputStyle, paddingRight: '2rem'}}
                    />
                    <span style={inputSuffixStyle}>%</span>
                  </div>
                </div>
              </div>

              <div style={buttonGroupStyle}>
                <button
                  type="submit"
                  style={calculateButtonStyle}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  {t('calculateButton')}
                </button>
                <button
                  type="button"
                  style={clearButtonStyle}
                  onClick={handleClear}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#e2e8f0'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#f1f5f9'}
                >
                  {t('clearButton')}
                </button>
              </div>
            </form>
          </div>

          {results && (
            <div>
              <div style={resultsStyle} id="results-section">
                <h3 style={resultsH3Style}>{t('comparisonResultsTitle')}</h3>

                <div style={{ overflowX: 'auto' }}>
                  <table style={tableStyle}>
                    <thead>
                      <tr>
                        <th style={thStyle}></th>
                        <th style={thStyle}>{t('traditionalIraColumn')}</th>
                        <th style={thStyle}>{t('rothIraColumn')}</th>
                        <th style={thStyle}>{t('taxableSavingsColumn')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={tdStyle}>{t('balanceAtRetirementRow')}</td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.traditionalBalance)}</td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.rothBalance)}</td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.taxableBalance)}</td>
                      </tr>
                      <tr style={{ background: '#fef3c7' }}>
                        <td style={{...tdStyle, fontWeight: 600}}><strong>{t('afterTaxBalanceRow')}</strong></td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.traditionalAfterTax)}</td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.rothAfterTax)}</td>
                        <td style={{...tdStyle, ...amountValueStyle}}>{formatCurrency(results.taxableAfterTax)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div style={summaryBoxStyle}>
                  {(() => {
                    const tradVsRoth = results.traditionalAfterTax - results.rothAfterTax;
                    const rothVsTaxable = results.rothAfterTax - results.taxableAfterTax;

                    return (
                      <>
                        <p style={summaryPStyle}>
                          {tradVsRoth > 0 ? (
                            <>
                              {t('summaryTraditionalBetter')} <strong style={{ color: '#047857', fontWeight: 700 }}>{formatCurrency(Math.abs(tradVsRoth))}</strong> {t('summaryMoreThan')} {inputs.retirementAge}.
                            </>
                          ) : tradVsRoth < 0 ? (
                            <>
                              {t('summaryRothBetter')} <strong style={{ color: '#047857', fontWeight: 700 }}>{formatCurrency(Math.abs(tradVsRoth))}</strong> {t('summaryMoreThan')} {inputs.retirementAge}.
                            </>
                          ) : (
                            <>
                              {t('summaryEqual')} {inputs.retirementAge}.
                            </>
                          )}
                        </p>
                        <p style={summaryPStyle}>
                          {t('summaryRothVsTaxable')} <strong style={{ color: '#047857', fontWeight: 700 }}>{formatCurrency(rothVsTaxable)}</strong> {t('summaryMoreThanTaxable')}
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}
        </div>

        {results && chartData.length > 0 && (
          <div style={chartContainerStyle}>
            <h3 style={chartTitleStyle}>{t('chartTitle')}</h3>
            <div style={chartLegendStyle}>
              <div style={legendItemStyle}>
                <div style={{...legendColorStyle, background: '#2563eb'}}></div>
                <span>{t('legendTraditionalBefore')}</span>
              </div>
              <div style={legendItemStyle}>
                <div style={{...legendColorStyle, background: '#10b981'}}></div>
                <span>{t('legendTraditionalAfter')}</span>
              </div>
              <div style={legendItemStyle}>
                <div style={{...legendColorStyle, background: '#dc2626'}}></div>
                <span>{t('legendRoth')}</span>
              </div>
              <div style={legendItemStyle}>
                <div style={{...legendColorStyle, background: '#0891b2'}}></div>
                <span>{t('legendTaxable')}</span>
              </div>
            </div>
            <svg
              width="100%"
              height="400"
              viewBox="0 0 800 400"
              style={{ maxWidth: '100%', height: 'auto' }}
            >
              {(() => {
                const width = 800;
                const height = 400;
                const padding = { top: 40, right: 40, bottom: 60, left: 80 };
                const chartWidth = width - padding.left - padding.right;
                const chartHeight = height - padding.top - padding.bottom;

                const maxBalance = Math.max(...chartData.map(d => d.traditional));
                const minAge = chartData[0].age;
                const maxAge = chartData[chartData.length - 1].age;

                const xScale = (age: number) => padding.left + ((age - minAge) / (maxAge - minAge)) * chartWidth;
                const yScale = (balance: number) => padding.top + chartHeight - (balance / maxBalance) * chartHeight;

                const gridLines = 5;
                const ageStep = Math.ceil((maxAge - minAge) / 8);

                return (
                  <>
                    {Array.from({ length: gridLines + 1 }).map((_, i) => {
                      const y = padding.top + (chartHeight / gridLines) * i;
                      const value = maxBalance * (1 - i / gridLines);
                      return (
                        <g key={`grid-y-${i}`}>
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={width - padding.right}
                            y2={y}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                          />
                          <text
                            x={padding.left - 10}
                            y={y}
                            textAnchor="end"
                            dominantBaseline="middle"
                            fill="#6b7280"
                            fontSize="12"
                          >
                            {formatCurrency(value)}
                          </text>
                        </g>
                      );
                    })}

                    {Array.from({ length: Math.floor((maxAge - minAge) / ageStep) + 1 }).map((_, i) => {
                      const age = minAge + i * ageStep;
                      const x = xScale(age);
                      return (
                        <g key={`grid-x-${i}`}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={height - padding.bottom}
                            stroke="#e5e7eb"
                            strokeWidth="1"
                          />
                          <text
                            x={x}
                            y={height - padding.bottom + 20}
                            textAnchor="middle"
                            fill="#6b7280"
                            fontSize="12"
                          >
                            {age}
                          </text>
                        </g>
                      );
                    })}

                    <path
                      d={chartData.map((point, index) => {
                        const x = xScale(point.age);
                        const y = yScale(point.traditional);
                        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#2563eb"
                      strokeWidth="3"
                    />

                    <path
                      d={chartData.map((point, index) => {
                        const x = xScale(point.age);
                        const y = yScale(point.traditionalAfterTax);
                        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#10b981"
                      strokeWidth="3"
                    />

                    <path
                      d={chartData.map((point, index) => {
                        const x = xScale(point.age);
                        const y = yScale(point.roth);
                        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#dc2626"
                      strokeWidth="3"
                    />

                    <path
                      d={chartData.map((point, index) => {
                        const x = xScale(point.age);
                        const y = yScale(point.taxable);
                        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
                      }).join(' ')}
                      fill="none"
                      stroke="#0891b2"
                      strokeWidth="3"
                    />
                  </>
                );
              })()}
            </svg>
          </div>
        )}
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
          <li><strong style={strongStyle}>{t('insight5Title')}</strong> {t('insight5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('traditionalIraTitle')}</h3>
        <p style={paragraphStyle}>{t('traditionalIraDescription')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('rothIraTitle')}</h3>
        <p style={paragraphStyle}>{t('rothIraDescription')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('taxableSavingsTitle')}</h3>
        <p style={paragraphStyle}>{t('taxableSavingsDescription')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.4rem' : '1.5rem', marginTop: '1.5rem'}}>{t('keyConsiderationsTitle')}</h3>
        <ul style={listStyle}>
          <li><strong style={strongStyle}>{t('consideration1Title')}</strong> {t('consideration1Description')}</li>
          <li><strong style={strongStyle}>{t('consideration2Title')}</strong> {t('consideration2Description')}</li>
          <li><strong style={strongStyle}>{t('consideration3Title')}</strong> {t('consideration3Description')}</li>
          <li><strong style={strongStyle}>{t('consideration4Title')}</strong> {t('consideration4Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem'}}>{t('faq1Question')}</h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem'}}>{t('faq2Question')}</h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem'}}>{t('faq3Question')}</h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem'}}>{t('faq4Question')}</h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{...h2Style, fontSize: screenSize === 'mobile' ? '1.15rem' : screenSize === 'tablet' ? '1.3rem' : '1.4rem', marginTop: '1.5rem'}}>{t('faq5Question')}</h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ul style={listStyle}>
          <li><a href="https://www.tiaa.org/public/tools-calculators" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>{t('reference1')}</a></li>
          <li><a href="https://www.fidelity.com/calculators-tools/retirement-calculator/overview" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>{t('reference2')}</a></li>
          <li><a href="https://www.thrivent.com/insights/tools/roth-ira-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>{t('reference3')}</a></li>
          <li><a href="https://www.aarp.org/money/retirement/traditional-ira-calculator/" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>{t('reference4')}</a></li>
          <li><a href="https://www.schwab.com/retirement-planning-tools/retirement-calculator" target="_blank" rel="noopener noreferrer" style={{ color: colors.primary, textDecoration: 'none' }}>{t('reference5')}</a></li>
        </ul>
      </section>
    </div>
  );
}